import React, { useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import { LoadingState, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BarChart3, Users, PauseCircle, CalendarDays, RefreshCw, FileText, FileSpreadsheet } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";

const PALETTE = ["#3AAA35", "#1976D2", "#F57C00", "#0B5D3B", "#D92D20", "#0B3A75", "#7E57C2", "#00897B"];

const periodoLabel = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
};

const Kpi = ({ icon: Icon, label, value, color, bg, testid }) => (
  <Card className="p-4 min-w-0" data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: bg }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-bold leading-none" style={{ color }}>{value}</div>
        <div className="text-xs text-muted-foreground mt-1 truncate">{label}</div>
      </div>
    </div>
  </Card>
);

export const CruscottoPresenze = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [da, setDa] = useState("");
  const [a, setA] = useState("");
  const [exporting, setExporting] = useState("");

  const esporta = async (formato) => {
    setExporting(formato);
    try {
      const params = { formato };
      if (da) params.da = da;
      if (a) params.a = a;
      const res = await api.get("/accessi/statistiche/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (formato === "pdf") {
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 15000);
      } else {
        const link = document.createElement("a");
        link.href = url; link.download = "cruscotto_presenze.xlsx";
        document.body.appendChild(link); link.click(); link.remove();
        URL.revokeObjectURL(url);
      }
    } catch (e) { toast.error(extractError(e, "Errore nell'esportazione")); } finally { setExporting(""); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (da) params.da = da;
      if (a) params.a = a;
      const res = await api.get("/accessi/statistiche", { params });
      setData(res.data);
    } catch (e) { toast.error(extractError(e)); } finally { setLoading(false); }
  }, [da, a]);
  useEffect(() => { load(); }, [load]);

  const perPeriodo = (data?.per_periodo || []).map((p) => ({ ...p, label: periodoLabel(p.periodo) }));
  const perEvento = data?.per_evento || [];
  const perGruppo = data?.per_gruppo || [];
  const totali = data?.totali || { presenze: 0, sospesi: 0, eventi: 0 };
  const hasData = totali.presenze > 0 || totali.sospesi > 0;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div><Label className="text-xs">Dal</Label><Input type="date" value={da} onChange={(e) => setDa(e.target.value)} data-testid="cruscotto-da" /></div>
        <div><Label className="text-xs">Al</Label><Input type="date" value={a} onChange={(e) => setA(e.target.value)} data-testid="cruscotto-a" /></div>
        <Button variant="outline" onClick={load} data-testid="cruscotto-aggiorna"><RefreshCw className="h-4 w-4 mr-2" /> Aggiorna</Button>
        <div className="sm:ml-auto flex gap-2">
          <Button variant="outline" onClick={() => esporta("pdf")} disabled={!!exporting} data-testid="cruscotto-export-pdf">
            {exporting === "pdf" ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />} PDF
          </Button>
          <Button variant="outline" onClick={() => esporta("xlsx")} disabled={!!exporting} data-testid="cruscotto-export-xlsx">
            {exporting === "xlsx" ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />} Excel
          </Button>
        </div>
      </div>

      {loading ? <LoadingState /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Kpi icon={Users} label="Presenze registrate" value={totali.presenze} color="var(--brand-green-700)" bg="var(--brand-green-100)" testid="cruscotto-kpi-presenze" />
            <Kpi icon={PauseCircle} label="Verifiche in sospeso" value={totali.sospesi} color="var(--brand-orange-600)" bg="var(--brand-orange-100)" testid="cruscotto-kpi-sospesi" />
            <Kpi icon={CalendarDays} label="Eventi coinvolti" value={totali.eventi} color="var(--brand-blue-700)" bg="var(--brand-blue-100)" testid="cruscotto-kpi-eventi" />
          </div>

          {!hasData ? (
            <EmptyState title="Nessun dato nel periodo" description="Registra presenze o modifica l'intervallo di date per vedere le statistiche." />
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="p-4 min-w-0" data-testid="cruscotto-chart-evento">
                  <div className="flex items-center gap-2 mb-3"><BarChart3 className="h-4 w-4 text-[color:var(--brand-green-700)]" /><h3 className="font-semibold">Affluenza per evento</h3></div>
                  <ResponsiveContainer width="100%" height={Math.max(220, perEvento.length * 34)}>
                    <BarChart data={perEvento} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#EEF1F5" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="nome" width={130} tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: "#F5F7FA" }} />
                      <Bar dataKey="presenze" name="Presenze" fill="#3AAA35" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                <Card className="p-4 min-w-0" data-testid="cruscotto-chart-gruppo">
                  <div className="flex items-center gap-2 mb-3"><Users className="h-4 w-4 text-[color:var(--brand-blue-700)]" /><h3 className="font-semibold">Presenze per gruppo</h3></div>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={perGruppo} dataKey="presenze" nameKey="gruppo" cx="50%" cy="50%" outerRadius={90} label={{ fontSize: 11 }}>
                        {perGruppo.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </div>

              <Card className="p-4 min-w-0" data-testid="cruscotto-chart-periodo">
                <div className="flex items-center gap-2 mb-3"><CalendarDays className="h-4 w-4 text-[color:var(--brand-blue-600)]" /><h3 className="font-semibold">Andamento presenze per periodo</h3></div>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={perPeriodo} margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="presenze" name="Presenze" stroke="#1976D2" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
};
